import type { ReactNode } from 'react';
import { DirectionProvider } from '@radix-ui/react-direction';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { isApiProblem } from '@shared/api/problem';
import { useDirection } from '@shared/hooks/useLocale';
import { i18n } from '@shared/lib/i18n/i18n';
import { Toaster, TooltipProvider } from '@shared/ui';

/** Query defaults per codebase doc §12: no retries on 4xx, 30 s stale time. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: (count, error) => !(isApiProblem(error) && error.status >= 400 && error.status < 500) && count < 2,
        refetchOnWindowFocus: true,
      },
      mutations: { retry: false },
    },
  });
}

export const queryClient = createQueryClient();

function DirectionBridge({ children }: { children: ReactNode }) {
  // Radix primitives (menus, tabs, tooltips) follow the document direction for arrow keys and placement.
  const dir = useDirection();
  return <DirectionProvider dir={dir}>{children}</DirectionProvider>;
}

export function AppProviders({ children, client = queryClient }: { children: ReactNode; client?: QueryClient }) {
  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={client}>
        <DirectionBridge>
          <TooltipProvider delayDuration={300}>
            <Toaster>{children}</Toaster>
          </TooltipProvider>
        </DirectionBridge>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
